import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { ISchemaId, IId, ISchemaAndId } from '../../types/common'
import {
  TimeslotBody,
  TimeslotUpdateBody,
  TimeslotResponse,
  AttandancesResponse,
  AttendanceBody,
  AttandanceResponse,
} from '@backend/timeslots/timeslotsRouter'
import { prepareHeaders, handleApiError } from './helpers'

export enum ETimeslotType {
  Disabled = 'DISABLED',
  Important = 'IMPORTANT',
  Required = 'REQUIRED',
}

export enum ETimeslotIdTypes {
  User = 'USER',
  Schema = 'SCHEMA',
  Scene = 'SCENE',
  SceneGroup = 'SCENE_GROUP',
}

export interface ITimeslot extends TimeslotResponse {}

interface ITimeslotCreate extends ISchemaId {
  parentType: ETimeslotIdTypes
  parentId: number
  timeslots: TimeslotBody[]
}

interface ITimeslotUpdate extends IId {
  parentType: ETimeslotIdTypes
  parentId: number
  timeslot: TimeslotUpdateBody
}

interface IAttendanceCreate extends AttendanceBody, ISchemaId {}

export interface IAttendance extends AttandanceResponse {}

export const timeslotsApi = createApi({
  reducerPath: 'timeslots',
  baseQuery: fetchBaseQuery({
    baseUrl: window.env.API_ENDPOINT,
    prepareHeaders,
  }),
  endpoints: (builder) => ({
    addTimeslots: builder.query<void, ITimeslotCreate>({
      query: ({ schemaId, parentType, parentId, timeslots }) => ({
        url: `timeslots/schema/${schemaId}/${parentType}/${parentId}`,
        method: 'POST',
        body: timeslots.map(
          (timeslot): TimeslotBody => ({
            ...timeslot,
            comment: timeslot.comment.trim(),
          }),
        ),
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.timeslots.errors.addTimeslots', err)
        }
      },
    }),
    updateTimeslot: builder.query<void, ITimeslotUpdate>({
      query: ({ parentType, parentId, id, timeslot }) => ({
        url: `timeslots/timeslot/${parentType}/${parentId}/${id}`,
        method: 'PATCH',
        body: {
          ...timeslot,
          comment: timeslot.comment.trim(),
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.timeslots.errors.updateTimeslot', err)
        }
      },
    }),
    deleteTimeslots: builder.query<void, ISchemaId & { ids: number[] }>({
      query: ({ schemaId, ids }) => ({
        url: `timeslots/schema/${schemaId}`,
        method: 'DELETE',
        body: { ids },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.timeslots.errors.deleteTimeslots', err)
        }
      },
    }),
    addAttendance: builder.query<void, IAttendanceCreate>({
      query: ({ schemaId, userId, timeslotId }) => ({
        url: `timeslots/schema/${schemaId}/attendances`,
        method: 'POST',
        body: {
          userId,
          timeslotId,
        },
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.addAttendance', err)
        }
      },
    }),
    getAttendances: builder.query<
      AttandancesResponse,
      { schemaId: number; day: string }
    >({
      query: ({ schemaId, day }) => ({
        url: `timeslots/schema/${schemaId}/attendances/timeslot/${day}`,
        method: 'GET',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.getAttendance', err)
        }
      },
    }),
    removeAttendance: builder.query<void, ISchemaAndId>({
      query: ({ schemaId, id }) => ({
        url: `timeslots/schema/${schemaId}/attendances/attendance/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_id, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          handleApiError('api.schemas.errors.removeAttendance', err)
        }
      },
    }),
  }),
})

export const {
  useLazyAddTimeslotsQuery,
  useLazyUpdateTimeslotQuery,
  useLazyDeleteTimeslotsQuery,
  useLazyAddAttendanceQuery,
  useLazyGetAttendancesQuery,
  useLazyRemoveAttendanceQuery,
} = timeslotsApi
